import React, { Component, Children, ReactChild } from "react";
import {
  StepWizardHostProps,
  StepWizardHostState,
  StepWizardNavChildProps,
  StepState,
} from "./WizardStepper.types";
import { WizardNavBar } from "./WizardNavBar/WizardNavBar";
import { WizardPagination } from "./WizardPagination/WizardPagination";
import { setFocusOnElementById } from "../../utils/helpers";

type childType = React.ReactElement<any>;

/**
 * Handles all the logic for Wizard Stepper.
 */
export class StepWizardHost extends Component<
  StepWizardHostProps,
  StepWizardHostState
> {
  static defaultProps = {
    hidePagination: false,
    children: [],
    initialStep: 1,
    thankYouStep: -1,
    onStepChange: () => {},
  };

  constructor(props) {
    super(props);
    this.state = this.initialState();
  }

  /** Setup Steps */
  initialState = () => {
    const state: StepWizardHostState = {
      activeStep: 0,
      classes: {},
      // Transition Classes
      transitions: {
        enterRight: "animated fadeInRight",
        enterLeft: "animated fadeInLeft",
        exitRight: "animated fadeOutRight",
        exitLeft: "animated fadeOutLeft",
      },
      steps: [],
    };
    state.transitions.intro = "";

    // Set initial classes
    Children.forEach(this.props.children, (child: childType, i) => {
      // Hide steps by default
      state.classes[i] = "hide";
      state.steps.push({
        stepId: i,
        isDone: false,
        stepLabel: "",
      });
    });

    // Set activeStep to initialStep if exists
    const initialStep = this.props.initialStep - 1;
    if (initialStep && this.props.children[initialStep]) {
      state.activeStep = initialStep;
    }

    // Give initial step an intro class
    state.classes[state.activeStep] = state.transitions.intro;

    return state;
  };

  isInvalidStep = (next) =>
    next < 0 || next >= (this.props.children as ReactChild[]).length;

  setActiveStep = (next: number) => {
    const active = this.state.activeStep;
    // console.log('change', active, next);
    if (active === next) return;
    if (this.isInvalidStep(next)) {
      console.error(`${next + 1} is an invalid step`);
      return;
    }

    const { classes, transitions } = this.state;

    if (active < next) {
      // slide left
      classes[active] = `hide exitLeft`;
      classes[next] = transitions.enterRight;
    } else {
      // slide right
      classes[active] = `hide exitRight`;
      classes[next] = transitions.enterLeft;
    }

    this.setState(
      {
        activeStep: next,
        classes,
      },
      () => {
        // Step change callback
        // todo change to help with managinging children from outer parent?

        setFocusOnElementById("step" + (next + 1));
        window.scrollTo(0, 0);

        this.onStepChange({
          previousStep: active + 1,
          activeStep: next + 1,
          totalSteps: this.state.steps.length,
        });
      }
    );
  };

  onStepChange = (stats) => {
    // User callback
    this.props.onStepChange(stats);
  };

  /** Go to first step */
  firstStep = () => this.goToStep(0);

  /** Go to last step */
  lastStep = () => this.goToStep(this.props.children.length - 1);

  /** Next Step */
  nextStep = () => this.setActiveStep(this.state.activeStep + 1);

  /** Previous Step */
  previousStep = () => this.setActiveStep(this.state.activeStep - 1);

  /** Go to step index */
  goToStep = (step) => this.setActiveStep(step);

  onChildChanged = (state: StepState, stepId: number) => {
    this.setState((prevState) => {
      return {
        steps: prevState.steps
          .map((oldState) => {
            if (oldState.stepId === stepId) {
              return { ...oldState, ...state };
            } else return { ...oldState };
          })
          .slice(),
      };
    });
  };

  decorateSteps = (steps: StepState[]) => {
    const { stepValidationMap } = this.props;
    const newSteps = steps.map((s, i) => {
      const step = { ...s };
      step.isDone =
        stepValidationMap &&
        stepValidationMap[i] &&
        stepValidationMap[i].isValid;
      return step;
    });
    return newSteps;
  };

  render() {
    const { stepValidationMap } = this.props;
    const navProps: StepWizardNavChildProps = {
      currentStep: this.state.activeStep,
      totalSteps: this.props.children.length,
      steps: this.decorateSteps(this.state.steps),
      nextLabel: this.props.nextLabel,
      previousLabel: this.props.previousLabel,
      hidePagination: this.props.hidePagination,
      thankYouStep: this.props.thankYouStep,
      /** Functions */
      nextStep: this.nextStep,
      previousStep: this.previousStep,
      goToStep: this.goToStep,
      firstStep: this.firstStep,
      lastStep: this.lastStep,
    };

    const { classes } = this.state;

    const childrenWithProps = React.Children.map(
      this.props.children,
      (child: childType, i) => {
        const stepState: StepState = {
          isActive: i === this.state.activeStep,
          stepId: i,
          isDone:
            stepValidationMap &&
            stepValidationMap[i] &&
            stepValidationMap[i].isValid,
          isThankYouPage: i === this.props.thankYouStep,
        };
        const childProps = {
          transitions: classes[i],
          reportToWizard: childCurrying(i, this.onChildChanged),
          stepState,
          navProps,
        };
        return (
          <Step {...childProps} key={i}>
            {React.cloneElement(child, childProps)}
          </Step>
        );
      }
    );

    return (
      <div className="wizardstepper-host">
        <WizardNavBar {...navProps} />
        {childrenWithProps}
        <WizardPagination {...navProps} />
      </div>
    );
  }
}

export const Step = ({ children, transitions = "" }) => (
  <div className={`step ${transitions}`}>{children}</div>
);

function childCurrying(
  stepId: number,
  caller: (state: StepState, stepId: number) => void
) {
  return function (state: StepState) {
    caller(state, stepId);
  };
}
